import { useEffect, useState } from "react";
import { Package, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", category: "structures", quantity: 1 };

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/equipment").then((r) => setItems(r.data.filter((e) => !e.archived))).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, category: item.category, quantity: item.quantity });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, quantity: Number(form.quantity) };
    try {
      if (editing) await api.patch(`/equipment/${editing.id}`, payload);
      else await api.post("/equipment", payload);
      toast.success(editing ? "Equipment updated" : "Equipment added");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name} permanently?`)) return;
    try {
      await api.delete(`/equipment/${item.id}`);
      toast.success("Equipment deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="equipment-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Equipment</h1>
          <p className="mt-1 text-sm text-muted-foreground">Hire inventory with live stock control — assignments to active bookings count against stock.</p>
        </div>
        <Button data-testid="new-equipment-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> Add Equipment
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {items.map((e) => {
          const total = e.quantity || 0;
          const assigned = e.assigned || 0;
          const available = e.available ?? total;
          const pct = total ? Math.min(100, (assigned / total) * 100) : 0;
          return (
            <div key={e.id} data-testid={`equipment-card-${e.id}`} className="border border-border bg-white p-5 transition-transform duration-200 hover:-translate-y-[2px]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-muted"><Package className="h-5 w-5 text-ink" /></div>
                <div>
                  <h3 className="font-heading text-sm font-bold tracking-tight">{e.name}</h3>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground capitalize">{e.category}</p>
                </div>
              </div>

              <div className="mt-4">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-semibold uppercase tracking-[0.15em] text-muted-foreground">Stock</span>
                  <span className={`font-bold tabular-nums ${available === 0 ? "text-red-600" : ""}`} data-testid={`equipment-stock-${e.id}`}>
                    {available} of {total} available
                  </span>
                </div>
                <div className="h-1.5 w-full bg-muted">
                  <div className={`h-full ${available === 0 ? "bg-red-500" : "bg-ink"}`} style={{ width: `${pct}%` }} />
                </div>
                {assigned > 0 && <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`equipment-assigned-${e.id}`}>{assigned} assigned to active bookings</p>}
              </div>

              <div className="mt-4 flex gap-2 border-t border-border pt-4">
                <Button data-testid={`edit-equipment-${e.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openEdit(e)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button data-testid={`delete-equipment-${e.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => remove(e)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="col-span-4 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No equipment in inventory yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm bg-white" data-testid="equipment-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Equipment" : "Add Equipment"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="equipment-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="equipment-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="structures">Structures</SelectItem>
                  <SelectItem value="power">Power</SelectItem>
                  <SelectItem value="furniture">Furniture</SelectItem>
                  <SelectItem value="kitchen">Kitchen</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Quantity in stock</Label><Input data-testid="equipment-quantity-input" type="number" min="1" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
            <Button data-testid="equipment-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editing ? "Save Changes" : "Add Equipment"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
